/**
 * RollbackDialog component.
 * Modal dialog for rolling back to a previous configuration version.
 * Requires a reason for the rollback for audit purposes.
 */

import { useState, useCallback, useEffect } from "react";

import type { RollbackDialogProps } from "./types";

/**
 * RollbackDialog component for reverting to previous config versions.
 */
export function RollbackDialog({
  isOpen,
  onClose,
  verticalKey,
  configType,
  targetVersion,
  currentVersion,
  onRollback,
  isRollingBack,
}: RollbackDialogProps): React.ReactElement | null {
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Reset state when dialog opens
  useEffect(() => {
    if (isOpen) {
      setReason("");
      setError(null);
    }
  }, [isOpen]);

  // Check if rollback is allowed
  const hasReason = reason.trim().length >= 10;
  const isRollbackEnabled = hasReason && !isRollingBack;

  // Handle rollback
  const handleRollback = useCallback(async (): Promise<void> => {
    if (!isRollbackEnabled) return;

    setError(null);

    try {
      await onRollback(reason.trim());
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Rollback failed");
    }
  }, [isRollbackEnabled, reason, onRollback, onClose]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape" && isOpen && !isRollingBack) {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, isRollingBack, onClose]);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      data-testid="rollback-dialog"
    >
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <WarningIcon className="w-6 h-6 text-amber-500" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Rollback Configuration
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isRollingBack}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50"
            aria-label="Close dialog"
          >
            <CloseIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4">
          {/* Warning message */}
          <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
            <p className="text-sm text-amber-800 dark:text-amber-300">
              This will roll back{" "}
              <strong>
                {verticalKey}/{configType}
              </strong>{" "}
              from version <strong>v{currentVersion}</strong> to version{" "}
              <strong>v{targetVersion}</strong>. This action cannot be undone automatically.
            </p>
          </div>

          {/* Version comparison */}
          <div className="mb-4 flex items-center justify-center gap-4">
            <div className="text-center">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Current</p>
              <span className="inline-block px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full text-sm font-medium">
                v{currentVersion}
              </span>
            </div>
            <ArrowIcon className="w-6 h-6 text-gray-400" />
            <div className="text-center">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Target</p>
              <span className="inline-block px-3 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded-full text-sm font-medium">
                v{targetVersion}
              </span>
            </div>
          </div>

          {/* Reason input */}
          <div className="mb-4">
            <label
              htmlFor="rollback-reason"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              Reason for Rollback <span className="text-red-500">*</span>
            </label>
            <textarea
              id="rollback-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Explain why you are rolling back (minimum 10 characters)..."
              maxLength={500}
              rows={3}
              disabled={isRollingBack}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                         bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100
                         focus:ring-2 focus:ring-amber-500 focus:border-amber-500
                         disabled:opacity-50 disabled:bg-gray-100 dark:disabled:bg-gray-800"
            />
            <div className="flex justify-between mt-1">
              <p className="text-xs text-gray-500">
                {reason.length < 10 && (
                  <span className="text-amber-600">
                    {10 - reason.length} more characters required
                  </span>
                )}
              </p>
              <p className="text-xs text-gray-500">{reason.length}/500</p>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
          <button
            type="button"
            onClick={onClose}
            disabled={isRollingBack}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300
                       hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleRollback}
            disabled={!isRollbackEnabled}
            className="px-4 py-2 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700
                       rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isRollingBack && <SpinnerIcon className="w-4 h-4 animate-spin" />}
            {isRollingBack ? "Rolling Back..." : "Rollback"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Icons
// ============================================================================

function CloseIcon({ className }: { readonly className: string }): React.ReactElement {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function SpinnerIcon({ className }: { readonly className: string }): React.ReactElement {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

function WarningIcon({ className }: { readonly className: string }): React.ReactElement {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
      />
    </svg>
  );
}

function ArrowIcon({ className }: { readonly className: string }): React.ReactElement {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M13 7l5 5m0 0l-5 5m5-5H6"
      />
    </svg>
  );
}

export default RollbackDialog;
